// NoteEditor - Create/Edit personal notes for places (Enhanced Version)

import React, { useState, useMemo } from 'react';
import { X, Star, Calendar, Smile, Save, Camera, Eye, EyeOff } from 'lucide-react';
import type { PlaceNote, MainCategory, AnyCategory, PhotoMetadata } from '../../types';
import { Timestamp } from '@firebase/firestore';
import { MoodSelector } from './MoodSelector';
import { NotePhotoUploader } from './NotePhotoUploader';
import { migrateLegacyMood } from '../../constants/moods';

interface NoteEditorProps {
  placeId: string;
  placeName: string;
  placeCategory?: AnyCategory;
  userId: string;
  existingNote?: PlaceNote;
  onSave: (note: {
    text: string;
    personalRating?: number;
    visitDate?: Date;
    mood?: string;        // Legacy (backward compatibility)
    moods?: string[];     // New: multiple moods
    photoMetadata?: PhotoMetadata[];
    isPublic?: boolean;
  }) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export function NoteEditor({
  placeId,
  placeName,
  placeCategory,
  userId,
  existingNote,
  onSave,
  onCancel,
  isLoading = false,
}: NoteEditorProps) {
  const [text, setText] = useState(existingNote?.text || '');
  const [personalRating, setPersonalRating] = useState<number | undefined>(
    existingNote?.personalRating
  );
  const [visitDate, setVisitDate] = useState<string>(
    existingNote?.visitDate
      ? (existingNote.visitDate instanceof Timestamp
          ? existingNote.visitDate.toDate()
          : new Date(existingNote.visitDate)
        ).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0]
  );

  // Moods: Use existing moods array or migrate legacy single mood
  const initialMoods = useMemo(() => {
    if (existingNote?.moods && existingNote.moods.length > 0) {
      return existingNote.moods;
    }
    return migrateLegacyMood(existingNote?.mood);
  }, [existingNote]);

  const [moods, setMoods] = useState<string[]>(initialMoods);

  // Photos
  const [photoMetadata, setPhotoMetadata] = useState<PhotoMetadata[]>(
    existingNote?.photoMetadata || []
  );

  // Privacy
  const [isPublic, setIsPublic] = useState(existingNote?.isPublic || false);

  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [showPhotos, setShowPhotos] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!text.trim()) return;

    await onSave({
      text: text.trim(),
      personalRating,
      visitDate: visitDate ? new Date(visitDate) : undefined,
      mood: moods[0], // Legacy: first mood for backward compatibility
      moods,
      photoMetadata: photoMetadata.length > 0 ? photoMetadata : undefined,
      isPublic,
    });
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden max-h-[90vh] overflow-y-auto">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">
          {existingNote ? 'Notiz bearbeiten' : 'Neue Notiz'}
        </h3>
        <button
          onClick={onCancel}
          className="p-1 hover:bg-gray-200 rounded transition"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-4 space-y-5">
        {/* Place Info */}
        <div className="text-sm text-gray-500">
          Notiz für: <span className="font-medium text-gray-700">{placeName}</span>
        </div>

        {/* Visit Date */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-1">
            <Calendar className="w-4 h-4" />
            Besuchsdatum
          </label>
          <input
            type="date"
            value={visitDate}
            onChange={(e) => setVisitDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-indigo-500"
          />
        </div>

        {/* Personal Rating */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
            <Star className="w-4 h-4" />
            Persönliche Bewertung (optional)
          </label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setPersonalRating(personalRating === star ? undefined : star)}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(null)}
                className="p-1 transition"
              >
                <Star
                  className={`w-6 h-6 transition ${
                    star <= (hoverRating || personalRating || 0)
                      ? 'text-amber-400 fill-amber-400'
                      : 'text-gray-300'
                  }`}
                />
              </button>
            ))}
            {personalRating && (
              <span className="ml-2 text-sm text-gray-500 self-center">
                {personalRating} von 5
              </span>
            )}
          </div>
        </div>

        {/* Mood Selector (Enhanced) */}
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
            <Smile className="w-4 h-4" />
            Stimmung (optional)
          </label>
          <MoodSelector
            selectedMoods={moods}
            onChange={setMoods}
            maxMoods={5}
          />
        </div>

        {/* Photos Section */}
        <div>
          <button
            type="button"
            onClick={() => setShowPhotos(!showPhotos)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2 hover:text-indigo-600 transition-colors"
          >
            <Camera className="w-4 h-4" />
            Fotos hinzufügen
            {photoMetadata.length > 0 && (
              <span className="bg-indigo-100 text-indigo-700 text-xs px-2 py-0.5 rounded-full">
                {photoMetadata.length}
              </span>
            )}
          </button>

          {showPhotos && (
            <NotePhotoUploader
              userId={userId}
              noteId={existingNote?.id}
              photos={photoMetadata}
              onChange={setPhotoMetadata}
              maxPhotos={10}
            />
          )}
        </div>

        {/* Note Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Deine Notiz
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Was hast du erlebt? Was war besonders? Wen hast du getroffen? ..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-indigo-500 resize-none"
            required
          />
        </div>

        {/* Privacy Toggle */}
        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2">
            {isPublic ? (
              <Eye className="w-4 h-4 text-indigo-500" />
            ) : (
              <EyeOff className="w-4 h-4 text-gray-500" />
            )}
            <div>
              <p className="text-sm font-medium text-gray-700">
                {isPublic ? 'Öffentlich' : 'Privat'}
              </p>
              <p className="text-xs text-gray-500">
                {isPublic
                  ? 'Andere können diesen Eintrag auf deinem Profil sehen'
                  : 'Nur für dich sichtbar'
                }
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsPublic(!isPublic)}
            className={`
              relative w-12 h-6 rounded-full transition-colors
              ${isPublic ? 'bg-indigo-500' : 'bg-gray-300'}
            `}
          >
            <span
              className={`
                absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform
                ${isPublic ? 'translate-x-7' : 'translate-x-1'}
              `}
            />
          </button>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={!text.trim() || isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Speichern...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Speichern
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default NoteEditor;
